/* The Pottery Lounge · modern layer: sticky header state, marquees, the glaze
   ribbon and scroll reveals. Progressive enhancement only; nothing here is
   required for navigation, buying or booking. */
(() => {
  const doc = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Sticky frosted header: the announcement bar scrolls away, the header stays. */
  function stickyHeader() {
    const group = document.querySelector('.shopify-section-group-header-group');
    if (!group || !group.querySelector('.pl-header')) return;
    const announcement = group.querySelector('.pl-announcement');
    const offset = () => (announcement ? announcement.offsetHeight : 0);
    const measure = () => doc.style.setProperty('--pl-announcement-h', `${offset()}px`);
    measure();
    window.addEventListener('resize', measure, { passive: true });
    doc.classList.add('pl-sticky-header');
    let queued = false;
    const update = () => {
      queued = false;
      doc.classList.toggle('pl-scrolled', window.scrollY > offset() + 4);
    };
    window.addEventListener('scroll', () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  /* Turn a row of short items into a seamless marquee. The duplicate half is
     hidden from assistive technology and keyboard focus. */
  function marquee(strip) {
    if (strip.dataset.plMarquee || strip.children.length < 3) return;
    strip.dataset.plMarquee = 'on';
    const group = document.createElement('div');
    group.className = 'pl-marquee__group';
    [...strip.children].forEach((item) => group.appendChild(item));
    const clone = group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    const track = document.createElement('div');
    track.className = 'pl-marquee__track';
    track.append(group, clone);
    strip.append(track);
    strip.classList.add('pl-marquee');
  }

  /* A slow ribbon of glaze tiles under the glaze library heading. Decorative:
     the catalogue itself reports its own loading problems. */
  async function glazeRibbon() {
    const root = document.querySelector('[data-pl-catalogue]');
    const heading = root && root.querySelector('.pl-shop-heading');
    if (!root || !heading || !root.dataset.source || root.querySelector('.pl-glaze-ribbon')) return;
    let data;
    try {
      const response = await fetch(root.dataset.source);
      if (!response.ok) return;
      data = await response.json();
    } catch {
      return;
    }
    if (root.querySelector('.pl-glaze-ribbon')) return;
    const images = (data.products || []).map((p) => p.images && p.images[0]).filter(Boolean).slice(0, 16);
    if (images.length < 8) return;
    const makeGroup = () => {
      const group = document.createElement('div');
      group.className = 'pl-glaze-ribbon__group';
      images.forEach((src) => {
        const tile = document.createElement('span');
        tile.className = 'pl-glaze-ribbon__tile';
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'eager';
        img.setAttribute('fetchpriority', 'low');
        img.decoding = 'async';
        img.width = 160;
        img.height = 160;
        tile.append(img);
        group.append(tile);
      });
      return group;
    };
    const ribbon = document.createElement('div');
    ribbon.className = 'pl-glaze-ribbon';
    ribbon.setAttribute('aria-hidden', 'true');
    const track = document.createElement('div');
    track.className = 'pl-glaze-ribbon__track';
    track.append(makeGroup(), makeGroup());
    ribbon.append(track);
    heading.after(ribbon);
  }

  /* Reveal headings, cards and images as they scroll into view. Only content
     that starts below the fold is touched, so nothing above it ever hides. */
  function reveals() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    const fold = window.innerHeight * 0.95;
    const candidates = document.querySelectorAll('main h2, main .eyebrow, main .pl-library-card, main .pl-more-ways article, main section img, main .benefits');
    const items = [...candidates].filter((el) => {
      if (el.closest('dialog, .pl-glaze-ribbon, .pl-sticky-buy, [data-pl-catalogue] .pl-library-grid')) return false;
      if (el.tagName === 'IMG' && el.closest('.pl-library-card, .pl-more-ways article')) return false;
      return el.getBoundingClientRect().top > fold;
    });
    if (!items.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => {
      const siblings = el.parentElement ? [...el.parentElement.children] : [];
      el.style.setProperty('--pl-delay', `${Math.min(Math.max(siblings.indexOf(el), 0), 5) * 80}ms`);
      el.classList.add(el.tagName === 'IMG' ? 'pl-reveal-img' : 'pl-reveal');
      observer.observe(el);
    });
  }

  /* Live availability from the booking system: the next open sessions, as
     links that open the booking form with that day and time already chosen.
     If the booking system can't be reached, these spots simply stay hidden. */
  async function nextSessions() {
    const boxes = [...document.querySelectorAll('[data-pl-availability]')];
    const lines = [...document.querySelectorAll('[data-pl-next-line]')];
    const first = boxes[0] || lines[0];
    if (!first || !first.dataset.endpoint) return;
    let data;
    try {
      const response = await fetch(first.dataset.endpoint, { credentials: 'omit' });
      if (!response.ok) return;
      data = await response.json();
    } catch {
      return;
    }
    const sessions = [];
    (data.days || []).forEach((day) => (day.slots || []).forEach((slot) => sessions.push({ date: day.date, time: slot.time, available: slot.available })));
    if (!sessions.length) return;
    const dayLabel = (date) => new Intl.DateTimeFormat('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
    const timeLabel = (time) => new Intl.DateTimeFormat('en-NZ', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(`2026-01-01T${time}:00Z`));
    const placesLabel = (n) => (n <= 4 ? `Only ${n} left` : `${n} places`);
    const linkFor = (base, s) => {
      const url = new URL(base, window.location.href);
      url.searchParams.set('date', s.date);
      url.searchParams.set('time', s.time);
      return url.href;
    };
    const span = (className, text) => {
      const el = document.createElement('span');
      el.className = className;
      el.textContent = text;
      return el;
    };
    boxes.forEach((box) => {
      const list = box.querySelector('ul');
      if (!list) return;
      list.replaceChildren(...sessions.slice(0, 6).map((s) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = linkFor(box.dataset.bookUrl, s);
        link.setAttribute('aria-label', `${dayLabel(s.date)}, ${timeLabel(s.time)}, ${placesLabel(s.available)}`);
        link.append(span('pl-slot__day', dayLabel(s.date)), span('pl-slot__time', timeLabel(s.time)), span(`pl-slot__places${s.available <= 4 ? ' is-low' : ''}`, placesLabel(s.available)));
        item.append(link);
        return item;
      }));
      box.hidden = false;
    });
    lines.forEach((line) => {
      const s = sessions[0];
      const link = document.createElement('a');
      link.href = linkFor(line.dataset.bookUrl, s);
      const dot = span('pl-live-dot', '');
      dot.setAttribute('aria-hidden', 'true');
      link.append(dot, document.createTextNode(`Next available: ${dayLabel(s.date)}, ${timeLabel(s.time)} →`));
      line.replaceChildren(link);
      line.hidden = false;
    });
  }

  /* Gift cards: show each value as studio sessions. The one-session default
     is chosen by the product template, so price and cart always agree. */
  function giftCardSessions() {
    if (!document.querySelector('[data-pl-gift-card]')) return;
    const sessionPrice = 80;
    [...document.querySelectorAll('label.btn')].forEach((label) => {
      const match = label.textContent.trim().match(/^\$(\d+)(?:\.00)?$/);
      const amount = match && Number(match[1]);
      if (!amount || amount % sessionPrice !== 0) return;
      const count = amount / sessionPrice;
      const note = document.createElement('small');
      note.className = 'pl-sessions';
      note.textContent = `${count} ${count === 1 ? 'session' : 'sessions'}`;
      label.append(note);
    });
  }

  stickyHeader();
  giftCardSessions();
  nextSessions();
  if (!reduceMotion) {
    document.querySelectorAll('.pl-design .benefits').forEach(marquee);
    glazeRibbon();
  }
  reveals();
})();
